import { API_ENDPOINTS } from '../config';
import { LoginRequest, RegisterRequest, AuthResponse } from '../types/auth';

/**
 * Authentication service
 * Handles API calls for login, register, and token operations
 */

/**
 * Custom error class for authentication errors
 */
export class AuthenticationError extends Error {
  constructor(
    message: string,
    public statusCode?: number,
    public originalError?: any
  ) {
    super(message);
    this.name = 'AuthenticationError';
  }
}

/**
 * Make a POST request to the auth API
 */
const authFetch = async (url: string, body: any): Promise<AuthResponse> => {
  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    });

    // Check if response is ok (status 200-299)
    if (!response.ok) {
      let errorMessage = 'Authentication failed';
      
      try {
        const errorData = await response.json();
        errorMessage = errorData.message || errorData.error || errorMessage;
      } catch {
        // If can't parse error response, use status text
        errorMessage = response.statusText || errorMessage;
      }

      throw new AuthenticationError(errorMessage, response.status);
    }

    const data: AuthResponse = await response.json();
    return data;
  } catch (error) {
    if (error instanceof AuthenticationError) {
      throw error;
    }

    // Network or other errors
    console.error('Auth request failed:', error);
    throw new AuthenticationError(
      'Network error. Please check your connection.',
      undefined,
      error
    );
  }
};

/**
 * Login user with email and password
 * @throws AuthenticationError if login fails
 */
export const login = async (email: string, password: string): Promise<AuthResponse> => {
  const payload: LoginRequest = { email, password };
  return authFetch(API_ENDPOINTS.AUTH.LOGIN, payload);
};

/**
 * Register new user
 * @throws AuthenticationError if registration fails
 */
export const register = async (
  name: string,
  email: string,
  password: string
): Promise<AuthResponse> => {
  const payload: RegisterRequest = { name, email, password };
  return authFetch(API_ENDPOINTS.AUTH.REGISTER, payload);
};

/**
 * Validate token format (basic check)
 */
export const isValidTokenFormat = (token: string): boolean => {
  if (!token || typeof token !== 'string') return false;
  
  // JWT tokens have 3 parts separated by dots
  const parts = token.split('.');
  return parts.length === 3;
};

/**
 * Auth service object
 * Export as object for consistency with other services
 */
export const authService = {
  login,
  register,
  isValidTokenFormat,
};
